import React, { useEffect, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, CheckIcon, ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import {
  runnerApi, downloadBlob, errorData, errorText,
  type LeftoverDraft, type TemplateCategory, type TemplateDiff, type TemplateGroup, type TemplatePlan,
} from '../../../../api/migrationRunner';
import { Alert, Btn, Dialog, ErrorBox, Pill, Seg, Spinner, when } from '../ui';
import { CompanyHealthPanel, DriftDrawer } from '../HealthTab';
import { dbLabel, migName, plural, type CleanupCtx } from './shared';

type Filter = 'all' | TemplateCategory;

const GROUP_TEXT: Record<TemplateCategory, { title: string; sub: string }> = {
  template_behind: { title: 'The template is behind', sub: "Some companies already have these. The Schema Source doesn't, so the template doesn't either." },
  company_behind: { title: 'Companies are behind', sub: "The template has these; some companies don't." },
  leftover: { title: 'Leftovers', sub: 'No migration adds these. Usually a hand fix or a backup table.' },
};

/** Clean up › Template: differences grouped by item (approved mockup screen 2). */
const TemplateView: React.FC<{ ctx: CleanupCtx; diff: TemplateDiff | null; error: string | null; reload: () => Promise<void>; refreshKey: number }> = ({
  ctx, diff: baseDiff, error, reload, refreshKey,
}) => {
  const [filter, setFilter] = useState<Filter>('all');
  const [showIgnored, setShowIgnored] = useState(false);
  const [withIgnored, setWithIgnored] = useState<TemplateDiff | null>(null);
  const [byCompany, setByCompany] = useState(false);
  const [planOpen, setPlanOpen] = useState(false);
  const [driftFor, setDriftFor] = useState<string | null>(null);
  const [draft, setDraft] = useState<LeftoverDraft | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!showIgnored) { setWithIgnored(null); return; }
    runnerApi.templateDiff(true).then(setWithIgnored).catch((err) => setLocalError(errorText(err)));
  }, [showIgnored, baseDiff]);

  const diff = showIgnored ? withIgnored ?? baseDiff : baseDiff;
  const names = useMemo(() => Object.fromEntries((diff?.companies ?? []).map((c) => [c.databaseName, c.companyName])), [diff]);
  const sourceDb = diff?.source?.databaseName ?? null;
  const sourceName = sourceDb ? (diff?.source?.companyName || dbLabel(sourceDb, names)) : null;
  const groups = (diff?.groups ?? []).filter((g) => filter === 'all' || g.category === filter);
  const live = (diff?.groups ?? []).filter((g) => !g.ignored);
  const fixable = live.some((g) => g.category !== 'leftover');

  const after = async (text: string, undo?: () => Promise<void>) => {
    await reload();
    if (showIgnored) setWithIgnored(await runnerApi.templateDiff(true).catch(() => null));
    ctx.notify(text, undo);
    ctx.changed();
  };

  const ignore = async (g: TemplateGroup) => {
    setBusy(g.id);
    setLocalError(null);
    const items = g.has.flatMap((db) => g.items.map((item) => ({ databaseName: db, item })));
    try {
      await runnerApi.ignoreItems(items, `Clean up ${new Date().toLocaleDateString()}: leftover, fine to keep`);
      await after(`Ignored on ${plural(g.has.length, 'company', 'companies')}. Find it under Show ignored.`, async () => {
        await runnerApi.unignoreItems(items);
        await reload();
        ctx.notify('Undone.');
      });
    } catch (err) {
      setLocalError(errorText(err, 'Could not ignore'));
    } finally {
      setBusy(null);
    }
  };

  const unignore = async (g: TemplateGroup) => {
    setBusy(g.id);
    try {
      await runnerApi.unignoreItems(g.ignoredOn.flatMap((db) => g.items.map((item) => ({ databaseName: db, item }))));
      await after('No longer ignored.');
    } catch (err) {
      setLocalError(errorText(err));
    } finally {
      setBusy(null);
    }
  };

  const addToEveryone = async (g: TemplateGroup) => {
    setBusy(g.id);
    setLocalError(null);
    try {
      const items = g.kind === 'table' ? [{ table: g.table }] : g.columns.map((column) => ({ table: g.table, column }));
      setDraft(await runnerApi.draftLeftover(items, g.has[0]));
    } catch (err) {
      setLocalError(errorText(err, 'Could not draft the migration'));
    } finally {
      setBusy(null);
    }
  };

  const runOnSource = (g: TemplateGroup) => {
    if (!sourceDb) return;
    const migs = g.migrations.filter((m) => m.notAppliedOn.includes(sourceDb));
    ctx.nav.openBatchDialog({
      migrationIds: migs.map((m) => m.scriptId),
      targets: [sourceDb],
      onlyPairs: migs.map((m) => `${m.scriptId}|${sourceDb}`),
      title: `Run on ${sourceName} (Schema Source)`,
    });
  };

  const applyToLacking = (g: TemplateGroup) => {
    const pairs = g.migrations.flatMap((m) => m.notAppliedOn.filter((db) => g.lack.includes(db)).map((db) => `${m.scriptId}|${db}`));
    const targets = [...new Set(pairs.map((p) => p.split('|')[1]))];
    ctx.nav.openBatchDialog({
      migrationIds: [...new Set(g.migrations.map((m) => m.scriptId))],
      targets,
      onlyPairs: pairs,
      title: `Apply ${g.migrations.length === 1 ? migName(g.migrations[0].folderPath) : `${g.migrations.length} migrations`} to ${plural(targets.length, 'company', 'companies')}`,
    });
  };

  const actions = (g: TemplateGroup) => {
    const w = !ctx.isOwner;
    if (g.ignored) return <Btn kind="ghost" small disabled={w || busy === g.id || !ctx.ready} onClick={() => unignore(g)}>Stop ignoring</Btn>;
    if (g.category === 'template_behind') {
      const onSource = sourceDb ? g.migrations.some((m) => m.notAppliedOn.includes(sourceDb)) : false;
      return (
        <>
          {onSource && <Btn small disabled={w} onClick={() => runOnSource(g)}>Run on {sourceName}</Btn>}
          {g.migrations.slice(0, 2).map((m) => (
            <Btn key={m.scriptId} kind="ghost" small onClick={() => ctx.nav.openPreview(m.scriptId)}>Preview</Btn>
          ))}
        </>
      );
    }
    if (g.category === 'company_behind') {
      if (g.migrations.length) return <Btn small disabled={w} onClick={() => applyToLacking(g)}>Apply to {g.lack.length}</Btn>;
      return (
        <div className="flex flex-wrap justify-end gap-1.5">
          {g.lack.slice(0, 6).map((db) => (
            <Btn key={db} kind="ghost" small disabled={w} onClick={() => setDriftFor(db)}>Fix drift on {dbLabel(db, names)}</Btn>
          ))}
        </div>
      );
    }
    return (
      <>
        <Btn kind="ghost" small disabled={w || busy === g.id || !ctx.ready} onClick={() => ignore(g)}>It's fine, ignore</Btn>
        <Btn kind="ghost" small disabled={busy === g.id} onClick={() => addToEveryone(g)}>Add to everyone</Btn>
      </>
    );
  };

  const why = (g: TemplateGroup) => {
    const m = g.migrations[0];
    if (g.ignored) return `Marked fine on ${g.ignoredOn.map((d) => dbLabel(d, names)).join(', ')}.`;
    if (g.category === 'template_behind') {
      if (g.templateStale) return `${sourceName} (the Schema Source) already has it; the template file is older. Re-dump it on the Templates tab.`;
      return `Added by ${migName(m.folderPath)}${g.migrations.length > 1 ? ` (+${g.migrations.length - 1})` : ''}, which never ran on ${sourceName ?? 'the Schema Source'}${sourceName ? ' (the Schema Source)' : ''}. Running it there puts it in the template.`;
    }
    if (g.category === 'company_behind') {
      if (!m) return `No migration that adds it is pending on ${g.lack.map((d) => dbLabel(d, names)).join(', ')}. Fix drift copies it from the template.`;
      return `${migName(m.folderPath)} never ran on ${g.lack.map((d) => dbLabel(d, names)).join(', ')}.`;
    }
    return `No migration adds ${g.kind === 'table' ? 'this table' : g.columns.length > 1 ? 'these columns' : 'this column'}. ${g.has.length === 1 ? 'Only this company has it.' : ''}`;
  };

  const what = (g: TemplateGroup) => (g.kind === 'table' ? g.table : `${g.table}.${g.columns.join(', ')}`);
  const kindLabel = (g: TemplateGroup) => (g.kind === 'table' ? 'table' : g.columns.length > 1 ? `${g.columns.length} columns` : 'column');

  const section = (cat: TemplateCategory) => {
    const list = groups.filter((g) => g.category === cat);
    if (!list.length) return null;
    return (
      <section key={cat} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 px-4 py-3">
          <b className="text-slate-900">{GROUP_TEXT[cat].title}</b> <Pill tone="mut">{list.length}</Pill>
          <div className="text-[12.5px] text-slate-500">{GROUP_TEXT[cat].sub}</div>
        </div>
        <ul className="divide-y divide-slate-100">
          {list.map((g) => (
            <li key={g.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <span className="break-all font-mono text-[12.5px] font-bold text-slate-900">{what(g)}</span>{' '}
                <Pill tone={g.inTemplate ? 'warn' : 'info'}>{g.inTemplate ? 'missing' : 'extra'} · {kindLabel(g)}</Pill>
                {g.ignored && <> <Pill tone="mut">ignored</Pill></>}
                <div className="mt-1 text-[12.5px] text-slate-500">{why(g)}</div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {(diff?.companies ?? []).filter((c) => c.state === 'ok').map((c) => {
                    const has = g.has.includes(c.databaseName);
                    return (
                      <span key={c.databaseName} className={`rounded-md border px-1.5 py-px text-[11.5px] ${has ? 'border-transparent bg-green-50 text-green-700' : 'border-slate-200 text-slate-400'}`}>
                        {has ? 'has · ' : ''}{c.companyName}{c.isSource ? ' (source)' : ''}
                      </span>
                    );
                  })}
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-1.5">{actions(g)}</div>
            </li>
          ))}
        </ul>
      </section>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-bold text-slate-900">Template differences</h2>
          <p className="text-[12.5px] text-slate-500">
            {sourceName
              ? <>Template = {sourceName} (the Schema Source). It updates itself whenever a migration finishes on {sourceName}.</>
              : 'No Schema Source is set, so the template never refreshes itself. Pick one on the Templates tab.'}
            {diff && <> Checked {when(diff.checkedAt)}.</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Seg<Filter>
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'All' },
              { value: 'template_behind', label: 'Template is behind' },
              { value: 'company_behind', label: 'Company is behind' },
              { value: 'leftover', label: 'Leftovers' },
            ]}
          />
          <label className="flex items-center gap-1.5 text-[12.5px] text-slate-600">
            <input type="checkbox" checked={showIgnored} onChange={(e) => setShowIgnored(e.target.checked)} /> Show ignored
          </label>
          <Btn kind="ghost" small onClick={() => setByCompany((v) => !v)}>{byCompany ? 'By item' : 'By company'}</Btn>
          <Btn kind="ghost" small onClick={() => reload()}>Re-check</Btn>
        </div>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}
      {localError && <ErrorBox>{localError}</ErrorBox>}
      {diff?.source && !diff.source.reachable && <Alert tone="warn" title="The Schema Source can't be read">Its server didn't answer, so "the Schema Source has it" can't be told.</Alert>}

      {byCompany ? (
        <CompanyHealthPanel nav={ctx.nav} isOwner={ctx.isOwner} refreshKey={refreshKey} />
      ) : (
        <>
          {!diff && !error && <div className="flex items-center gap-2 text-sm text-slate-500"><Spinner /> Reading every company's tables…</div>}
          {diff && fixable && (
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl bg-blue-50 p-4">
              <div className="min-w-0 flex-1">
                <b className="text-slate-900">Bring everyone to the full set</b>
                <div className="text-[13px] text-slate-700">Runs the migrations that are missing on the companies that lack them (the Schema Source first, so the template catches up). Then everyone has everything. Leftovers are left alone.</div>
              </div>
              <Btn disabled={!ctx.isOwner} onClick={() => setPlanOpen(true)}>Preview the plan</Btn>
            </div>
          )}
          {(['template_behind', 'company_behind', 'leftover'] as TemplateCategory[]).map(section)}
          {diff && !groups.length && (
            <div className="rounded-xl border border-slate-200 bg-white p-4 text-[13px] text-slate-500">
              Nothing here. {live.length ? 'Try another filter.' : 'Every company matches the template.'}
            </div>
          )}
        </>
      )}

      <PlanDialog open={planOpen} onClose={() => setPlanOpen(false)} ctx={ctx} names={names} />
      <DriftDrawer databaseName={driftFor} onClose={() => setDriftFor(null)} nav={ctx.nav} isOwner={ctx.isOwner} onRan={() => { reload(); ctx.changed(); }} />
      <DraftDialog draft={draft} onClose={() => setDraft(null)} />
    </div>
  );
};

/** "Bring everyone to the full set": the plan, a dry-run summary, one batch. */
const PlanDialog: React.FC<{ open: boolean; onClose: () => void; ctx: CleanupCtx; names: Record<string, string> }> = ({ open, onClose, ctx, names }) => {
  const [plan, setPlan] = useState<TemplatePlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPlan(null);
    setError(null);
    runnerApi.templatePlan().then(setPlan).catch((err) => setError(errorText(err, 'Could not build the plan')));
  }, [open]);

  const nm = (db: string) => dbLabel(db, { ...names, ...(plan?.names ?? {}) });
  const run = async () => {
    if (!plan) return;
    setBusy(true);
    setError(null);
    try {
      const b = await runnerApi.runTemplatePlan(plan.planHash);
      onClose();
      ctx.notify('Batch started. The template catches up after the Schema Source step.');
      ctx.changed();
      ctx.nav.openBatch(b.id);
    } catch (err) {
      const d = errorData<{ plan?: TemplatePlan }>(err);
      if (d?.code === 'PLAN_CHANGED' && d.plan) setPlan(d.plan);
      setError(errorText(err, 'Could not start the batch'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Bring everyone to the full set"
      footer={<><Btn kind="ghost" onClick={onClose}>Cancel</Btn><Btn onClick={run} disabled={busy || !plan || !plan.pairs.length}>{busy ? 'Starting…' : 'Run batch'}</Btn></>}
    >
      <p className="text-slate-500">One batch, in this order. Backups first (per Settings). Each company sees "Updating the system" while it runs.</p>
      {!plan && !error && <div className="flex items-center gap-2 text-slate-500"><Spinner /> Working out the plan and dry-running it…</div>}
      {plan && !plan.pairs.length && <Alert tone="ok" title="Nothing to run">Every company already has what a migration adds.</Alert>}
      {plan && plan.pairs.length > 0 && (
        <ol className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {plan.steps.map((s, i) => (
            <li key={i} className="px-3 py-2 text-[13px]">
              <b>Step {i + 1} · {s.targets.map(nm).join(', ')}{s.label ? ` (${s.label})` : ''}</b>
              <div className="text-slate-500">{s.migrations.map(migName).join(', ')}{s.label === 'Schema Source' ? '. Template refreshes after.' : ''}</div>
            </li>
          ))}
          <li className="px-3 py-2 text-[13px] text-slate-500">Leftovers are left alone. Decide those separately.</li>
        </ol>
      )}
      {plan?.dryRun && (
        <div className={`flex items-start gap-2 rounded-lg px-3 py-2 text-[13px] ${plan.dryRun.fail || plan.dryRun.errors ? 'bg-amber-50 text-amber-900' : 'bg-green-50 text-green-900'}`}>
          <CheckIcon className="mt-0.5 h-4 w-4 shrink-0" />
          Dry run: {plural(plan.dryRun.apply, 'statement')} would change things, {plan.dryRun.fail} would fail
          {plan.dryRun.unknown ? `, ${plan.dryRun.unknown} can't be checked` : ''}{plan.dryRun.errors ? `, ${plan.dryRun.errors} database(s) couldn't be read` : ''}.
        </div>
      )}
      {plan && !plan.dryRun && plan.pairs.length > 0 && <p className="text-xs text-slate-500">Too many pairs to dry-run here ({plan.pairs.length}); preview single migrations from the list.</p>}
      {plan && plan.driftOnly.length > 0 && (
        <Alert tone="info" title="Not in this batch: no migration adds these">
          {plan.driftOnly.map((d) => `${nm(d.databaseName)}: ${d.items.slice(0, 4).join(', ')}${d.items.length > 4 ? '…' : ''}`).join(' · ')}. Use Fix drift on those companies.
        </Alert>
      )}
      {error && <ErrorBox>{error}</ErrorBox>}
    </Dialog>
  );
};

/** The drafted "Add to everyone" migration: save it into CITS-Server/migrations and commit. */
const DraftDialog: React.FC<{ draft: LeftoverDraft | null; onClose: () => void }> = ({ draft, onClose }) => {
  const [copied, setCopied] = useState(false);
  useEffect(() => { setCopied(false); }, [draft]);
  return (
    <Dialog
      open={!!draft}
      onClose={onClose}
      title="Add to everyone: drafted migration"
      footer={draft && (
        <>
          <Btn kind="ghost" onClick={async () => { try { await navigator.clipboard.writeText(draft.sql); setCopied(true); } catch { /* blocked */ } }}>
            <ClipboardDocumentIcon className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
          </Btn>
          <Btn onClick={() => downloadBlob(draft.sql, draft.filename, 'application/sql')}><ArrowDownTrayIcon className="h-4 w-4" /> Download</Btn>
        </>
      )}
    >
      {draft && (
        <>
          <p>Save it as <b className="font-mono text-[12px]">CITS-Server/migrations/{draft.path}</b> and commit it. It then shows as pending on every company that lacks it; apply it from here or the Status tab. Nothing was written on the server.</p>
          {draft.notes.length > 0 && <Alert tone="warn" title="Check before committing">{draft.notes.join(' ')}</Alert>}
          <pre className="max-h-72 overflow-auto rounded-lg border border-slate-200 bg-slate-50 p-3 font-mono text-[11.5px] text-slate-800">{draft.sql}</pre>
        </>
      )}
    </Dialog>
  );
};

export default TemplateView;
